<template>
  <div class="page-container">
    <PageHeader icon="clients-active.svg" :title="pageTitle" />
    <div class="page-content">
      <div class="tab-content table">
        <TableComponent
          :columns="columns"
          :rows="tableRows_1"
          :title="'Менеджеры клиента'"
          :show-additional-table-header="false"
          :isLightFontAdditional="true"
          :actionType="'clientManagersManagement'"
          :clientManagersManagementCreate="userRole === 3"
          :noData="noData"
          :totalCount="totalCount"
          :pageSize="pageSize"
          :pageCount="pageCount"
          :currentPage="Number(currentPage)"
          :isLoading="loading"
          @regetData="regetData($event)"
          @downloadXSLX="downloadXSLX"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapMutations, mapState } from 'vuex'

  function pickName(obj) {
    return obj?.name || obj?.user?.name || ''
  }

  function parseCardNumbers(row) {
    if (typeof row.cards_ids === 'string') {
      return row.cards_ids
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    }
    if (Array.isArray(row.cards)) {
      return row.cards
        .map((c) => c.number || c.card_number)
        .filter(Boolean)
    }
    if (Array.isArray(row.cards_ids)) {
      return row.cards_ids
        .map((c) => (typeof c === 'string' || typeof c === 'number' ? c : c.number || c.card_number))
        .filter((c) => c != null && c !== '')
    }
    return []
  }

  function parseCardIdList(row) {
    if (Array.isArray(row.cards)) {
      return row.cards.map((c) => c.id).filter((id) => id != null)
    }
    if (Array.isArray(row.cards_ids)) {
      return row.cards_ids
        .map((c) => (typeof c === 'object' ? c.id : c))
        .filter((id) => id != null && typeof id === 'number')
    }
    return []
  }

  function resolveStatusId(row, userStatuses = []) {
    const user = row.user || row
    const statusId = user.status_id ?? row.status_id ?? row.status?.id
    if (statusId != null) {
      return statusId
    }
    const statusName = resolveStatus(row, userStatuses)
    if (statusName && userStatuses.length) {
      const found = userStatuses.find((s) => s.name === statusName)
      if (found) return found.id
    }
    return null
  }

  function resolveStatus(row, userStatuses = []) {
    if (typeof row.status === 'string') return row.status
    if (row.status?.name) return row.status.name
    const user = row.user || row
    if (typeof user.status === 'string') return user.status
    if (user.status?.name) return user.status.name
    const statusId = user.status_id ?? row.status_id
    if (statusId != null && userStatuses.length) {
      const found = userStatuses.find((s) => s.id === statusId)
      if (found) return found.name
    }
    return ''
  }

  function resolveType(row) {
    return row.user?.type ?? row.type ?? row.user?.type_id ?? null
  }

  function normalizeManagerRow(row, userStatuses = []) {
    const userType = resolveType(row)
    const userId = row.user?.id || row.id
    const status = resolveStatus(row, userStatuses)

    const customerName =
      row.customer_name ||
      row.customer?.name ||
      row.customer?.user?.name ||
      row.client?.name ||
      row.client?.user?.name ||
      ''

    const normalized = {
      id: userId,
      name: row.name || pickName(row.user) || pickName(row),
      email: row.email || row.user?.email || row.user?.login || '',
      customer_name: customerName,
      cards_ids: parseCardIdList(row),
      card_ids: parseCardIdList(row),
      cards_numbers: parseCardNumbers(row),
      status_id: resolveStatusId(row, userStatuses),
      status,
      type: userType,
      actionType: 'clientManagersManagement'
    }

    if (userType === 3) {
      if (row.customer_id != null) normalized.customer_id = row.customer_id
      if (row.user) normalized.user = row.user
      if (row.manager) normalized.manager = row.manager
    } else if (userType != null) {
      normalized.phone = row.phone || row.user?.phone || ''
    }

    return normalized
  }

  export default {
    name: 'ClientManagersManagement',
    data() {
      return {
        tableColumns_1: [
          {
            name: 'ID',
            field: 'id',
            filter_name: 'id',
            filter_value: '',
            order: ''
          },
          {
            name: 'Имя',
            field: 'name',
            filter_name: 'name',
            filter_value: '',
            order: ''
          },
          {
            name: 'Email',
            field: 'email',
            filter_name: 'email',
            filter_value: '',
            order: ''
          },
          {
            name: 'Статус',
            field: 'status',
            filter_name: 'status_id',
            filter_value: '',
            order: ''
          },
          {
            name: 'Клиент',
            field: 'customer_name',
            filter_name: 'customer_id',
            filter_value: '',
            order: '',
            multiple: true
          },
          {
            name: 'Список карт',
            field: 'cards_ids',
            filter_name: 'card',
            filter_value: '',
            order: '',
            expandable_list: true
          },
          {
            name: '',
            field: 'actions'
          }
        ],
        tableRows_1: [],
        noData: false,
        loading: false,
        totalCount: 0,
        pageSize: 0,
        pageCount: 0,
        currentPage: 0
      }
    },
    watch: {
      async allClientManagers() {
        await this.setTablesData()
      },
      async showRemoveUserPopup(val) {
        if (val === false) {
          await this.getAllClientManagers()
        }
      },
      async showResetPasswordPopup(val) {
        if (val === false) {
          await this.getAllClientManagers()
        }
      },
      async showClientManagerPopup(val) {
        if (val === false) {
          await this.getAllClientManagers()
        }
      }
    },
    computed: {
      ...mapState({
        allClientManagers: (state) => state.user.allClientManagers,
        columns: (state) => state.popups.columns,
        roles: (state) => state.user.roles,
        userRole: (state) => state.user.userRole,
        user_statuses: (state) => state.user.user_statuses,
        showRemoveUserPopup: (state) => state.popups.showRemoveUserPopup,
        showResetPasswordPopup: (state) => state.popups.showResetPasswordPopup,
        showClientManagerPopup: (state) => state.popups.showClientManagerPopup
      }),
      pageTitle() {
        return this.userRole === 3 ? 'Управление менеджерами' : 'Управление менеджерами клиента'
      },
      displayColumns() {
        if (this.userRole === 3) {
          return this.tableColumns_1.filter((c) => c.field !== 'customer_name')
        }
        return this.tableColumns_1
      }
    },
    methods: {
      ...mapActions({
        getAllClientManagers: 'getAllClientManagers',
        getUserRoles: 'getUserRoles',
        getUserStatuses: 'getUserStatuses'
      }),
      ...mapMutations({
        setColumns: 'SET_COLUMNS',
        setColumns_2: 'SET_COLUMNS_2',
        setPaginationFilters: 'SET_PAGINATION_FILTERS',
        resetPaginationFilters: 'RESET_PAGINATION_FILTERS'
      }),
      async downloadXSLX() {
        this.$store.commit('SET_DOWNLOAD_LOADING', true)
        await this.getAllClientManagers(true)
        this.$store.commit('SET_DOWNLOAD_LOADING', false)
      },
      async regetData(data) {
        this.loading = true

        const args = [
          {
            name: 'page',
            value: data.pageNumber
          },
          {
            name: 'perPage',
            value: data.size
          }
        ]

        if (this.totalCount && Math.ceil(this.totalCount / data.size) < data.pageNumber) {
          args[0].value = 1
        }

        this.setPaginationFilters(args)
        await this.getAllClientManagers()
      },
      getRowsFromPayload(payload) {
        if (Array.isArray(payload)) {
          return payload
        }
        if (payload && Array.isArray(payload.data)) {
          return payload.data
        }
        return []
      },
      async setTablesData() {
        const payload = this.allClientManagers
        const rows = this.getRowsFromPayload(payload).map((row) => {
          const normalized = normalizeManagerRow(row, this.user_statuses)
          const tableRow = {
            ...normalized,
            cards_ids: normalized.cards_numbers.length ? normalized.cards_numbers : normalized.cards_ids,
            cards_ids_show_all: false
          }
          if (tableRow.status === 'Активен') {
            tableRow.green = { status: true }
          } else {
            tableRow.red = { status: true }
          }
          return tableRow
        })

        if (payload && payload.total_count !== undefined) {
          this.totalCount = payload.total_count
          this.pageSize = payload.page_size
          this.pageCount = payload.page_count
          this.currentPage = payload.current_page
        } else {
          this.totalCount = rows.length
          this.pageSize = rows.length || 10
          this.pageCount = 1
          this.currentPage = 1
        }

        this.tableRows_1 = rows
        this.noData = rows.length === 0

        if (this.columns) {
          this.setColumns(
            this.userRole === 3
              ? this.columns.filter((c) => c.field !== 'customer_name')
              : this.columns
          )
        } else {
          this.setColumns(this.displayColumns)
        }
        this.loading = false
      }
    },
    async mounted() {
      this.setColumns(null)
      this.setColumns_2(null)
      this.resetPaginationFilters()

      if (!this.roles || this.roles.length === 0) {
        await this.getUserRoles()
      }
      if (!this.user_statuses || this.user_statuses.length === 0) {
        await this.getUserStatuses()
      }

      this.loading = true
      await this.getAllClientManagers()
    }
  }
</script>

<style scoped lang="scss">
  @import '../../styles/variables';

  .tab-content {
    margin-top: 30px;
  }
</style>
